import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import UseAxios from '../../../Api/http/http';
import { useAuth } from '../../../Hooks/useAuth';
import {
  getEffortEstimations,
  getResourceCategories,
  getResourceTypes,
  getProjectUnits,
  getProjectDetailsBySeqId,
  getProjectDetailsComplete,
  getProjectDetailsByProjectId,
  getProjectDetailsCompleteByProjectId,
  getCsatCustomerDetailsByProjectId,
  getEmployeeMasterList,
  getGrossMarginDataByProjectId,
  getEstimatedGmByProjectId,
  getProjectProgressByProjectId,
} from '../../../Api/request/projectDetails';
import { serviceName } from '../../../Api/resources/serviceConstants';

import {
  applyBackendDataToViewModel,
  buildEmptyBaseData,
  getResponseData,
  mergeProjectRecords,
  computeAuditScoreData,
  computeRiskRegisterData,
  computeResourceData,
  computeTbhResources,
} from './projectDetailsViewmodel';

/* eslint-disable id-length, max-lines */

const getDeliveryMethodologyDirectoryRequest = () => ({
  url: '/v1/delivery-methodology',
  method: 'get',
  headers: { 'content-type': 'application/json' },
  authRequired: false,
  csrfToken: true,
  service: serviceName.PROJECT_MS,
});

const REFERENCE_CACHE_TTL_MS = 5 * 60 * 1000;
const referenceDataCache = new Map();

const getCachedReferenceData = async (cacheKey, loader, ttlMs = REFERENCE_CACHE_TTL_MS) => {
  const now = Date.now();
  const cachedEntry = referenceDataCache.get(cacheKey);

  if (
    cachedEntry
    && Object.prototype.hasOwnProperty.call(cachedEntry, 'data')
    && (now - cachedEntry.timestamp) < ttlMs
  ) {
    return cachedEntry.data;
  }

  if (cachedEntry?.promise) {
    return cachedEntry.promise;
  }

  const requestPromise = Promise.resolve()
    .then(loader)
    .then((data) => {
      referenceDataCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
      return data;
    })
    .catch((error) => {
      referenceDataCache.delete(cacheKey);
      throw error;
    });

  referenceDataCache.set(cacheKey, {
    ...(cachedEntry && Object.prototype.hasOwnProperty.call(cachedEntry, 'data')
      ? cachedEntry
      : {}),
    promise: requestPromise,
    timestamp: cachedEntry?.timestamp || now,
  });

  return requestPromise;
};

const runOptionalRequest = async (loader) => {
  try {
    return { data: await loader(), error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// ---------------------------------------------------------------------------
// Helpers (module-level, no re-allocation on every render)
// ---------------------------------------------------------------------------

const resolveProjectIdentifierFromLocation = (locationSearch, queryKey, fallbackKeys) => {
  const queryParams = new URLSearchParams(locationSearch || '');
  const fallbackQueryKeys = Array.isArray(fallbackKeys) ? fallbackKeys : [];

  return (
    queryParams.get(queryKey)
    || fallbackQueryKeys.map((fallbackKey) => queryParams.get(fallbackKey)).find(Boolean)
  );
};

const extractBackendErrorMessage = (response) => (
  response?.data?.response?.data?.message
  || response?.data?.message
  || response?.message
  || 'Failed to fetch project list'
);

const isAxiosFailurePayload = (payload) => (
  Boolean(payload && (payload?.name === 'AxiosError' || payload?.isAxiosError))
);

const getRequestErrorMessage = (response, fallbackMessage) => (
  response?.data?.response?.data?.message
  || response?.data?.message
  || response?.message
  || fallbackMessage
);

const firstNonNullish = (...candidates) => candidates.find((value) => value !== null && value !== undefined);

const normalizeProjectResponseRecord = (record) => {
  if (!record || typeof record !== 'object') return record;

  const plainRecord = typeof record?.get === 'function'
    ? record.get({ plain: true })
    : (typeof record?.toJSON === 'function' ? record.toJSON() : record);

  const dataValues = plainRecord?.dataValues && typeof plainRecord.dataValues === 'object'
    ? plainRecord.dataValues
    : null;
  const nestedProject = plainRecord?.project && typeof plainRecord.project === 'object'
    ? plainRecord.project
    : null;
  const nestedProjectDataValues = nestedProject?.dataValues && typeof nestedProject.dataValues === 'object'
    ? nestedProject.dataValues
    : null;

  return {
    ...(plainRecord || {}),
    ...(nestedProjectDataValues || {}),
    ...(nestedProject || {}),
    ...(dataValues || {}),
    ...(record || {}),
  };
};

const extractEmployeeRows = (responseData) => {
  if (Array.isArray(responseData?.employeeList)) return responseData.employeeList;
  if (Array.isArray(responseData?.data?.employeeList)) return responseData.data.employeeList;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const extractCsatCustomerRows = (responseData) => {
  if (Array.isArray(responseData?.csatCustomerDetailsList)) return responseData.csatCustomerDetailsList;
  if (Array.isArray(responseData?.data?.csatCustomerDetailsList)) return responseData.data.csatCustomerDetailsList;
  if (Array.isArray(responseData?.customers)) return responseData.customers;
  if (Array.isArray(responseData?.data?.customers)) return responseData.data.customers;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const normalizeEmployeeDirectory = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => {
      const firstName = String(row.firstName ?? row.first_name ?? '').trim();
      const lastName = String(row.lastName ?? row.last_name ?? '').trim();
      const fullName = `${firstName} ${lastName}`.trim();

      return {
        emp_no: row.empNo ?? row.emp_no ?? null,
        emp_id: row.empId ?? row.emp_id ?? row.employee_id ?? null,
        email_id: row.emailId ?? row.email_id ?? row.email ?? null,
        name: fullName || row.fullName || row.full_name || row.name || row.label || row.emp_name || null,
        ctc: row.ctc ?? null,
      };
    })
    .filter((row) => row.emp_no !== null || row.emp_id !== null)
);

const normalizeCsatCustomerRows = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      customer_id: row.customer_id ?? row.id ?? row.customerId,
      customer_name: row.customer_name ?? row.name ?? row.customerName,
      project_id: row.project_id ?? row.projectId,
    }))
    .filter((row) => row.customer_id !== null && row.customer_id !== undefined && row.customer_name)
);

const extractDeliveryMethodologyRows = (responseData) => {
  if (Array.isArray(responseData?.deliveryMethodologyList)) return responseData.deliveryMethodologyList;
  if (Array.isArray(responseData?.deliveryMethodologyDetails)) return responseData.deliveryMethodologyDetails;
  if (Array.isArray(responseData?.data?.deliveryMethodologyList)) return responseData.data.deliveryMethodologyList;
  if (Array.isArray(responseData?.data?.deliveryMethodologyDetails)) {
    return responseData.data.deliveryMethodologyDetails;
  }
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const normalizeDeliveryMethodologyDirectory = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      method_id: row.method_id ?? row.methodId ?? row.delivery_methodology_id ?? row.id ?? null,
      methodology: row.methodology ?? row.delivery_methodology_name ?? row.name ?? row.label ?? null,
    }))
    .filter((row) => row.method_id !== null && row.method_id !== undefined && row.methodology)
);

const getNestedValue = (obj, path) => {
  if (!obj || typeof obj !== 'object' || !path) return undefined;
  return String(path).split('.')
    .filter(Boolean)
    .reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj);
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getUniqueNumericCandidates = (...values) => (
  values
    .map((value) => toNumberOrNull(value))
    .filter((value, index, arr) => value !== null && arr.indexOf(value) === index)
);

const extractEstimatedGmApiRows = (responseData) => {
  if (Array.isArray(responseData?.estimatedGmList)) return responseData.estimatedGmList;
  if (Array.isArray(responseData?.data?.estimatedGmList)) return responseData.data.estimatedGmList;
  if (Array.isArray(responseData?.estimated_gm)) return responseData.estimated_gm;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const extractGrossMarginApiRows = (responseData) => {
  if (Array.isArray(responseData?.grossMarginDataList)) return responseData.grossMarginDataList;
  if (Array.isArray(responseData?.data?.grossMarginDataList)) return responseData.data.grossMarginDataList;
  if (Array.isArray(responseData?.gross_margin_data)) return responseData.gross_margin_data;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const normalizeGrossMarginApiRows = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      gm_id: row.gm_id ?? row.gmId ?? null,
      project_id: row.project_id ?? row.projectId ?? null,
      gm_percent: row.gm_percent ?? row.gmPercent ?? null,
      current_dates: row.current_dates ?? row.currentDates ?? null,
      month_end_date: row.month_end_date ?? row.monthEndDate ?? null,
    }))
);

const normalizeEstimatedGmApiRows = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      estimated_id: row.estimated_id ?? row.estimatedId ?? null,
      project_id: row.project_id ?? row.projectId ?? null,
      gm_percent: row.gm_percent ?? row.gmPercent ?? null,
      updated_on: row.updated_on ?? row.updatedOn ?? null,
    }))
);

const extractProjectProgressApiRows = (responseData) => {
  if (Array.isArray(responseData?.projectProgressList)) return responseData.projectProgressList;
  if (Array.isArray(responseData?.data?.projectProgressList)) return responseData.data.projectProgressList;
  if (Array.isArray(responseData?.project_progress)) return responseData.project_progress;
  if (Array.isArray(responseData?.projectProgress)) return responseData.projectProgress;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const extractEffortEstimationApiRows = (responseData) => {
  if (Array.isArray(responseData?.effortEstimationList)) return responseData.effortEstimationList;
  if (Array.isArray(responseData?.data?.effortEstimationList)) return responseData.data.effortEstimationList;
  if (Array.isArray(responseData?.effortEstimations)) return responseData.effortEstimations;
  if (Array.isArray(responseData?.data?.effortEstimations)) return responseData.data.effortEstimations;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const extractEffortGroupsApiRows = (responseData) => {
  if (Array.isArray(responseData?.groups)) return responseData.groups;
  if (Array.isArray(responseData?.data?.groups)) return responseData.data.groups;
  if (Array.isArray(responseData?.effort_groups)) return responseData.effort_groups;
  return [];
};

const extractResourceTypeRows = (responseData) => {
  if (Array.isArray(responseData?.resourceTypeList)) return responseData.resourceTypeList;
  if (Array.isArray(responseData?.data?.resourceTypeList)) return responseData.data.resourceTypeList;
  if (Array.isArray(responseData?.resource_type)) return responseData.resource_type;
  if (Array.isArray(responseData?.resourceType)) return responseData.resourceType;
  if (Array.isArray(responseData?.resourceTypes)) return responseData.resourceTypes;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const extractResourceCategoryRows = (responseData) => {
  if (Array.isArray(responseData?.resourceCategoryList)) return responseData.resourceCategoryList;
  if (Array.isArray(responseData?.data?.resourceCategoryList)) return responseData.data.resourceCategoryList;
  if (Array.isArray(responseData?.resource_category)) return responseData.resource_category;
  if (Array.isArray(responseData?.resourceCategory)) return responseData.resourceCategory;
  if (Array.isArray(responseData?.resourceCategories)) return responseData.resourceCategories;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const extractProjectUnitRows = (responseData) => {
  if (Array.isArray(responseData?.projectUnitList)) return responseData.projectUnitList;
  if (Array.isArray(responseData?.data?.projectUnitList)) return responseData.data.projectUnitList;
  if (Array.isArray(responseData?.projectUnits)) return responseData.projectUnits;
  if (Array.isArray(responseData?.data?.projectUnits)) return responseData.data.projectUnits;
  if (Array.isArray(responseData?.project_units)) return responseData.project_units;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const splitEffortEstimationsByCrLink = (effortEstimations) => {
  const rows = (Array.isArray(effortEstimations) ? effortEstimations : [])
    .filter((row) => row && typeof row === 'object');

  return rows.reduce((acc, row) => {
    const crId = row?.cr_id ?? row?.crId;
    if (
      crId === null
      || crId === undefined
      || crId === ''
      || Number(crId) === 0
    ) {
      acc.initialRows.push(row);
    } else {
      acc.crLinkedRows.push(row);
    }
    return acc;
  }, { initialRows: [], crLinkedRows: [] });
};

const normalizeProjectProgressApiRows = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      progress_id: row.progress_id ?? row.progressId ?? row.id ?? null,
      project_id: row.project_id ?? row.projectId ?? null,
      date: row.date ?? null,
      progress_percent: row.progress_percent ?? row.progressPercent ?? null,
      remarks: row.remarks ?? '',
      is_delete: row.is_delete ?? row.isDelete ?? 0,
    }))
);

const pickLatestEstimatedGmRow = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      row,
      ts: row?.updated_on ? new Date(row.updated_on).getTime() : Number.NEGATIVE_INFINITY,
    }))
    .sort((firstRow, secondRow) => secondRow.ts - firstRow.ts)[0]?.row ?? null
);

const pickLatestGrossMarginRow = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      row,
      ts: row?.month_end_date
        ? new Date(row.month_end_date).getTime()
        : (row?.current_dates ? new Date(row.current_dates).getTime() : Number.NEGATIVE_INFINITY),
      gmId: toNumberOrNull(row?.gm_id) ?? Number.NEGATIVE_INFINITY,
    }))
    .sort((firstItem, secondItem) => {
      if (secondItem.ts !== firstItem.ts) return secondItem.ts - firstItem.ts;
      return secondItem.gmId - firstItem.gmId;
    })[0]?.row ?? null
);

const removeOverviewOnlyGmFallbacks = (project) => {
  if (!project || typeof project !== 'object') return project;

  const sanitized = { ...project };
  [
    'agmValue.gm_percent',
    'egmValue.gm_percent',
    'agm_gm_percent',
    'egm_gm_percent',
    'gmSummary',
    'agmValue',
    'egmValue',
    'gross_margin_data',
    'estimated_gm',
  ].forEach((key) => {
    if (key in sanitized) {
      delete sanitized[key];
    }
  });

  return sanitized;
};

const resolveMethodologyLabelFromSources = (sources, directory) => {
  const directoryMap = new Map(
    (Array.isArray(directory) ? directory : []).map((row) => [
      toNumberOrNull(row?.method_id),
      row?.methodology,
    ]),
  );

  const methodologyId = (Array.isArray(sources) ? sources : [])
    .map((source) => [
      source?.methodology,
      source?.delivery_methodology,
      getNestedValue(source, 'basicDetails.methodology'),
      getNestedValue(source, 'data.methodology'),
      getNestedValue(source, 'data.delivery_methodology'),
    ])
    .flat()
    .map((value) => toNumberOrNull(value))
    .find((value) => value !== null && directoryMap.has(value));

  return methodologyId === undefined ? null : directoryMap.get(methodologyId) || null;
};

const applyResolvedMethodologyToProjectHeader = (viewModel, methodologyLabel) => {
  if (!methodologyLabel || !Array.isArray(viewModel?.projectHeader?.basicDetails)) {
    return viewModel;
  }

  return {
    ...viewModel,
    projectHeader: {
      ...viewModel.projectHeader,
      basicDetails: viewModel.projectHeader.basicDetails.map((item) => (
        String(item?.label || '').trim()
          .toLowerCase() === 'methodology'
          ? { ...item, value: methodologyLabel }
          : item
      )),
    },
  };
};

const formatApiDateLabel = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = monthNames[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

const extractEstimatedGmRows = (source) => {
  const rows = [
    source?.estimated_gm,
    source?.estimatedGM,
    source?.egmValue,
    source?.gmSummary?.estimated_gm,
    source?.data?.estimated_gm,
    source?.data?.estimatedGM,
    source?.data?.egmValue,
  ].find(Array.isArray);

  return Array.isArray(rows) ? rows : [];
};

const resolveLatestEgmMetric = (sources, fallbackProjectIds = []) => {
  const projectIds = getUniqueNumericCandidates(
    ...(Array.isArray(fallbackProjectIds) ? fallbackProjectIds : [fallbackProjectIds]),
  );
  const rows = (Array.isArray(sources) ? sources : [])
    .flatMap((source) => extractEstimatedGmRows(source))
    .filter((row) => row && typeof row === 'object')
    .filter((row) => {
      if (!projectIds.length) return true;
      const rowProjectId = toNumberOrNull(row?.project_id);
      return rowProjectId !== null && projectIds.includes(rowProjectId);
    })
    .map((row) => ({
      row,
      ts: row?.updated_on ? new Date(row.updated_on).getTime() : Number.NEGATIVE_INFINITY,
    }))
    .sort((firstRow, secondRow) => secondRow.ts - firstRow.ts);

  const latest = rows[0]?.row;
  if (!latest) return null;

  const value = toNumberOrNull(
    latest?.gm_percent ?? latest?.gmPercent ?? latest?.gm_percentage ?? latest?.gmPercentage,
  );
  if (value === null) return null;

  return {
    value,
    label: formatApiDateLabel(latest?.updated_on)
      ? `EGM (On ${formatApiDateLabel(latest.updated_on)})`
      : 'EGM',
  };
};

const resolveOverviewEgmMetric = (sources) => {
  const rawValue = (Array.isArray(sources) ? sources : [])
    .map((source) => (
      source?.gmPercent?.egm
      ?? source?.['gmPercent.egm']
      ?? source?.egm
      ?? null
    ))
    .find((value) => value !== null && value !== undefined && value !== '');

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null;
  }

  if (typeof rawValue === 'string') {
    const trimmedValue = rawValue.trim();
    if (trimmedValue === '-' || trimmedValue.toUpperCase() === 'NA') {
      return {
        label: 'EGM',
        value: trimmedValue,
      };
    }
  }

  const numericValue = toNumberOrNull(rawValue);
  if (numericValue === null) {
    return null;
  }

  return {
    label: 'EGM',
    value: numericValue,
  };
};

const applyResolvedEgmToGmData = (viewModel, egmMetric) => {
  if (!viewModel || !Array.isArray(viewModel?.gmData) || !egmMetric) {
    return viewModel;
  }

  const hasEgm = viewModel.gmData.some((item) => String(item?.label || '').startsWith('EGM'));
  const nextGmData = hasEgm
    ? viewModel.gmData.map((item) => (
      String(item?.label || '').startsWith('EGM')
        ? { ...item, label: egmMetric.label, value: egmMetric.value }
        : item
    ))
    : [
      ...viewModel.gmData,
      { label: egmMetric.label, color: '#FF9A9A', value: egmMetric.value },
    ];

  return {
    ...viewModel,
    gmData: nextGmData,
  };
};

const fetchProjectDetails = async (projectSeqId, cacheBust = '') => {
  if (!projectSeqId) return { singleProject: null, completeProjectData: null };

  const isNumeric = /^\d+$/.test(String(projectSeqId).trim());
  const singleRequest = isNumeric
    ? getProjectDetailsBySeqId(projectSeqId, cacheBust)
    : getProjectDetailsByProjectId(projectSeqId, cacheBust);
  const completeRequest = isNumeric
    ? getProjectDetailsComplete(projectSeqId, cacheBust)
    : getProjectDetailsCompleteByProjectId(projectSeqId, cacheBust);

  const [singleResponse, completeResponse] = await Promise.all([
    UseAxios(singleRequest),
    UseAxios(completeRequest).catch((err) => err),
  ]);

  const singlePayload = singleResponse?.data;
  if (isAxiosFailurePayload(singlePayload)) {
    throw new Error(getRequestErrorMessage(singleResponse, 'Failed to fetch project details'));
  }

  const completePayload = completeResponse?.data;
  const completeProjectData = (
    completeResponse
    && !isAxiosFailurePayload(completePayload)
      ? getResponseData(completeResponse, 'complete project details')
      : null
  );

  return {
    singleProject: getResponseData(singleResponse, 'project details'),
    completeProjectData,
  };
};

const loadProjectDirectories = async () => {
  const [employeeDirectory, deliveryMethodologyDirectory] = await Promise.all([
    getCachedReferenceData('project-details-employee-master', async () => {
      const employeeResponse = await UseAxios(getEmployeeMasterList());
      return normalizeEmployeeDirectory(
        extractEmployeeRows(getResponseData(employeeResponse, 'employee master')),
      );
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to load employee directory:', err);
      return [];
    }),
    getCachedReferenceData('project-details-delivery-methodology', async () => {
      const deliveryMethodologyResponse = await UseAxios(getDeliveryMethodologyDirectoryRequest());
      return normalizeDeliveryMethodologyDirectory(
        extractDeliveryMethodologyRows(
          getResponseData(deliveryMethodologyResponse, 'delivery methodology'),
        ),
      );
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to load delivery methodology directory:', err);
      return [];
    }),
  ]);

  return { employeeDirectory, deliveryMethodologyDirectory };
};

const buildResolvedProjectIdentity = ({
  selectedFromList,
  singleProject,
  hydratedCompleteProjectData,
  viewModelWithResolvedEgm,
  resolvedProjectSeqId,
}) => ({
  actualProjectSeqId: firstNonNullish(
    selectedFromList?.project_seq_id,
    singleProject?.project_seq_id,
    hydratedCompleteProjectData?.project_seq_id,
    viewModelWithResolvedEgm?.project_seq_id,
    resolvedProjectSeqId,
    null,
  ),
  actualProjectId: firstNonNullish(
    selectedFromList?.project_id,
    singleProject?.project_id,
    hydratedCompleteProjectData?.project_id,
    viewModelWithResolvedEgm?.project_id,
    null,
  ),
});

const buildSupplementalProjectViewModel = ({
  selectedFromList,
  singleProject,
  completeProjectData,
  hydratedCompleteProjectData,
  employeeDirectory,
  deliveryMethodologyDirectory,
  estimatedGmRows,
  grossMarginRows,
  projectProgressRows,
}) => {
  const { viewModel: data, mergedBaseData } = buildViewModel({
    selectedFromList,
    singleProject,
    completeProjectData: hydratedCompleteProjectData,
    employeeDirectory,
    deliveryMethodologyDirectory,
    estimatedGmRows,
    grossMarginRows,
    projectProgressRows,
  });
  const resolvedMethodologyLabel = resolveMethodologyLabelFromSources(
    [selectedFromList, singleProject, completeProjectData],
    deliveryMethodologyDirectory,
  );
  const resolvedEgmMetric = resolveLatestEgmMetric(
    [
      selectedFromList,
      singleProject,
      hydratedCompleteProjectData,
      { estimated_gm: estimatedGmRows },
    ],
    [
      selectedFromList?.project_id,
      selectedFromList?.project_seq_id,
      singleProject?.project_id,
      singleProject?.project_seq_id,
      hydratedCompleteProjectData?.project_id,
      hydratedCompleteProjectData?.project_seq_id,
    ],
  );
  const resolvedOverviewEgmMetric = resolveOverviewEgmMetric([
    selectedFromList,
    singleProject,
    hydratedCompleteProjectData,
  ]);
  const viewModelWithMethodology = applyResolvedMethodologyToProjectHeader(
    data,
    resolvedMethodologyLabel,
  );

  const finalViewModel = applyResolvedEgmToGmData(
    viewModelWithMethodology,
    resolvedOverviewEgmMetric || resolvedEgmMetric,
  );

  return { viewModel: finalViewModel, mergedBaseData };
};

const getEmptyOptionalResult = (data = null) => Promise.resolve({ data, error: null });

const applySupplementalResultsToProjectData = ({
  effortResult,
  effortGroupsResult,
  hydratedCompleteProjectData,
  projectUnitsResult,
  resourceCategoryResult,
  resourceTypeResult,
}) => {
  let nextCompleteProjectData = typeof hydratedCompleteProjectData?.get === 'function'
    ? hydratedCompleteProjectData.get({ plain: true })
    : (typeof hydratedCompleteProjectData?.toJSON === 'function'
      ? hydratedCompleteProjectData.toJSON()
      : { ...(hydratedCompleteProjectData || {}) });

  if (effortResult?.error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load effort estimations:', effortResult.error);
  } else if (nextCompleteProjectData) {
    const effortRowsData = Array.isArray(effortResult?.data) ? effortResult.data : [];
    const effortGroupsData = Array.isArray(effortGroupsResult?.data) ? effortGroupsResult.data : [];

    if (effortRowsData.length || effortGroupsData.length) {
      const { initialRows } = splitEffortEstimationsByCrLink(effortRowsData);

      // Attach effortEstimationList, initialEffortEstimations, and groups without altering changeRequests
      nextCompleteProjectData = {
        ...nextCompleteProjectData,
        initialEffortEstimations: initialRows,
        effortEstimationList: effortRowsData,
        ...(effortGroupsData.length ? { groups: effortGroupsData } : {}),
      };
    }
  }

  if (resourceTypeResult?.error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load resource types:', resourceTypeResult.error);
  } else if (Array.isArray(resourceTypeResult?.data) && resourceTypeResult.data.length) {
    nextCompleteProjectData = {
      ...(nextCompleteProjectData && typeof nextCompleteProjectData === 'object'
        ? nextCompleteProjectData
        : {}),
      resource_type: resourceTypeResult.data,
    };
  }

  if (resourceCategoryResult?.error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load resource categories:', resourceCategoryResult.error);
  } else if (Array.isArray(resourceCategoryResult?.data) && resourceCategoryResult.data.length) {
    nextCompleteProjectData = {
      ...(nextCompleteProjectData && typeof nextCompleteProjectData === 'object'
        ? nextCompleteProjectData
        : {}),
      resource_category: resourceCategoryResult.data,
    };
  }

  if (projectUnitsResult?.error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load project units:', projectUnitsResult.error);
  } else if (Array.isArray(projectUnitsResult?.data)) {
    nextCompleteProjectData = {
      ...(nextCompleteProjectData && typeof nextCompleteProjectData === 'object'
        ? nextCompleteProjectData
        : {}),
      projectUnitList: projectUnitsResult.data,
    };
  }

  return nextCompleteProjectData;
};

const mergeProjectCoreSupplementalData = async ({
  resolvedProjectId,
}) => {
  const hasResolvedProjectId = (
    resolvedProjectId !== null
    && resolvedProjectId !== undefined
    && resolvedProjectId !== ''
  );

  const [grossMarginResult, estimatedGmResult] = await Promise.all([
    hasResolvedProjectId
      ? runOptionalRequest(async () => {
        const grossMarginResponse = await UseAxios(getGrossMarginDataByProjectId(resolvedProjectId));
        return normalizeGrossMarginApiRows(
          extractGrossMarginApiRows(getResponseData(grossMarginResponse, 'gross margin')),
        );
      })
      : getEmptyOptionalResult(),
    hasResolvedProjectId
      ? runOptionalRequest(async () => {
        const estimatedGmResponse = await UseAxios(getEstimatedGmByProjectId(resolvedProjectId));
        return normalizeEstimatedGmApiRows(
          extractEstimatedGmApiRows(getResponseData(estimatedGmResponse, 'estimated GM')),
        );
      })
      : getEmptyOptionalResult(),
  ]);

  if (grossMarginResult?.error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load gross margin:', grossMarginResult.error);
  }
  if (estimatedGmResult?.error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load estimated GM:', estimatedGmResult.error);
  }

  const estimatedGmRows = Array.isArray(estimatedGmResult?.data) ? estimatedGmResult.data : [];
  const grossMarginRows = Array.isArray(grossMarginResult?.data) ? grossMarginResult.data : [];

  return {
    estimatedGmRows,
    grossMarginRows,
  };
};

const buildViewModel = ({
  selectedFromList,
  singleProject,
  completeProjectData,
  employeeDirectory,
  deliveryMethodologyDirectory,
  estimatedGmRows,
  grossMarginRows,
  projectProgressRows,
}) => {
  const emptyBaseData = buildEmptyBaseData();
  const normalizedSelectedFromList = normalizeProjectResponseRecord(selectedFromList);
  const normalizedSingleProject = normalizeProjectResponseRecord(singleProject);
  const normalizedCompleteProjectData = normalizeProjectResponseRecord(completeProjectData);
  const sanitizedSelectedFromList = removeOverviewOnlyGmFallbacks(normalizedSelectedFromList);
  const normalizedEstimatedGmRows = normalizeEstimatedGmApiRows(estimatedGmRows);
  const normalizedGrossMarginRows = normalizeGrossMarginApiRows(grossMarginRows);
  const latestEstimatedGm = pickLatestEstimatedGmRow(normalizedEstimatedGmRows);
  const latestGrossMargin = pickLatestGrossMarginRow(normalizedGrossMarginRows);

  const mergedData = {
    ...emptyBaseData,
    ...(sanitizedSelectedFromList && typeof sanitizedSelectedFromList === 'object' ? sanitizedSelectedFromList : {}),
    ...(normalizedSingleProject && typeof normalizedSingleProject === 'object'
      ? normalizedSingleProject
      : {}),
    ...(normalizedCompleteProjectData && typeof normalizedCompleteProjectData === 'object'
      ? normalizedCompleteProjectData
      : {}),
    ...(normalizedEstimatedGmRows.length ? {
      estimated_gm: normalizedEstimatedGmRows,
      egmValue: normalizedEstimatedGmRows,
      egm_gm_percent: latestEstimatedGm?.gm_percent ?? null,
      'egmValue.gm_percent': latestEstimatedGm?.gm_percent ?? null,
    } : {}),
    ...(normalizedGrossMarginRows.length ? {
      gross_margin_data: normalizedGrossMarginRows,
      agmValue: latestGrossMargin,
      agm_gm_percent: latestGrossMargin?.gm_percent ?? null,
      'agmValue.gm_percent': latestGrossMargin?.gm_percent ?? null,
    } : {}),
    ...(Array.isArray(projectProgressRows) && projectProgressRows.length ? {
      project_progress: projectProgressRows,
    } : {}),
    employeeDirectory: Array.isArray(employeeDirectory) ? employeeDirectory : [],
    deliveryMethodologyDirectory: Array.isArray(deliveryMethodologyDirectory)
      ? deliveryMethodologyDirectory
      : [],
  };
  const selectedProject = mergeProjectRecords(
    sanitizedSelectedFromList,
    normalizedSingleProject,
    normalizedCompleteProjectData,
  );
  return {
    viewModel: applyBackendDataToViewModel(mergedData, selectedProject, { skipSectionComputation: true }),
    mergedBaseData: mergedData,
  };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/* eslint-disable-next-line max-lines-per-function */
const useProjectDetailsData = ({
  queryKey = 'projectId',
  fallbackKeys = ['id', 'projectSeqId', 'project_seq_id'],
} = {}) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Core base data state (loads on mount)
  const [baseDetails, setBaseDetails] = useState(null);

  // Lazy loaded CSAT section states
  const [csatCustomers, setCsatCustomers] = useState([]);
  const [csatCustomersLoading, setCsatCustomersLoading] = useState(false);
  const [csatCustomersError, setCsatCustomersError] = useState(null);
  const [csatCustomersLoaded, setCsatCustomersLoaded] = useState(false);
  const [csatCustomersLoadedProjectId, setCsatCustomersLoadedProjectId] = useState(null);
  const csatCustomersLoadPromiseRef = useRef(null);

  // Lazy loaded Progress section states
  const [progressRows, setProgressRows] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError, setProgressError] = useState(null);
  const [progressLoaded, setProgressLoaded] = useState(false);

  // Lazy loaded Efforts section states
  const [effortRows, setEffortRows] = useState(null);
  const [effortGroups, setEffortGroups] = useState(null);
  const [resourceTypes, setResourceTypes] = useState(null);
  const [resourceCategories, setResourceCategories] = useState(null);
  const [effortsLoading, setEffortsLoading] = useState(false);
  const [effortsError, setEffortsError] = useState(null);
  const [effortsLoaded, setEffortsLoaded] = useState(false);

  // Lazy loaded Project Units section states
  const [projectUnits, setProjectUnits] = useState(null);
  const [projectUnitsLoading, setProjectUnitsLoading] = useState(false);
  const [projectUnitsError, setProjectUnitsError] = useState(null);
  const [projectUnitsLoaded, setProjectUnitsLoaded] = useState(false);

  // Lazy loaded Audit section states
  const [auditSectionData, setAuditSectionData] = useState(null);
  const [auditSectionLoading, setAuditSectionLoading] = useState(false);
  const [auditSectionLoaded, setAuditSectionLoaded] = useState(false);

  // Lazy loaded Risk section states
  const [riskSectionData, setRiskSectionData] = useState(null);
  const [riskSectionLoading, setRiskSectionLoading] = useState(false);
  const [riskSectionLoaded, setRiskSectionLoaded] = useState(false);

  // Lazy loaded Resource (count + details + allocation) section states
  const [resourceSectionData, setResourceSectionData] = useState(null);
  const [resourceSectionLoading, setResourceSectionLoading] = useState(false);
  const [resourceSectionLoaded, setResourceSectionLoaded] = useState(false);

  // Lazy loaded TBH Resources section states
  const [tbhSectionData, setTbhSectionData] = useState(null);
  const [tbhSectionLoading, setTbhSectionLoading] = useState(false);
  const [tbhSectionLoaded, setTbhSectionLoaded] = useState(false);

  const fallbackKeysRef = useRef(fallbackKeys);
  useEffect(() => {
    fallbackKeysRef.current = fallbackKeys;
  }, [fallbackKeys]);

  useEffect(() => {
    let cancelled = false;

    const fetchProjectData = async () => {
      try {
        setLoading(true);

        const requestedProjectIdentifier = resolveProjectIdentifierFromLocation(
          globalThis.location.search,
          queryKey,
          fallbackKeysRef.current,
        );

        const requestCacheBust = String(Date.now());
        const resolvedProjectSeqId = requestedProjectIdentifier;

        const [detailsResult, directories] = await Promise.all([
          fetchProjectDetails(resolvedProjectSeqId, requestCacheBust),
          loadProjectDirectories(),
        ]);

        if (cancelled) return;

        const { singleProject, completeProjectData } = detailsResult;
        const { employeeDirectory, deliveryMethodologyDirectory } = directories;

        const resolvedProjectId = firstNonNullish(
          singleProject?.project_id,
          completeProjectData?.project_id,
          null,
        );

        const { estimatedGmRows, grossMarginRows } = await mergeProjectCoreSupplementalData({
          resolvedProjectId,
        });
        if (cancelled) return;

        const queryParams = new URLSearchParams(globalThis.location.search || '');
        let urlAccentColor = queryParams.get('accentColor') || queryParams.get('health') || '';

        if (!urlAccentColor) {
          try {
            if (resolvedProjectSeqId) {
              const seqColor = localStorage.getItem(`project_accent_${resolvedProjectSeqId}`);
              if (seqColor) urlAccentColor = seqColor;
            }
            if (!urlAccentColor && resolvedProjectId) {
              const idColor = localStorage.getItem(`project_accent_${resolvedProjectId}`);
              if (idColor) urlAccentColor = idColor;
            }
          } catch (storageError) {
            console.warn('Failed to retrieve accent color from localStorage', storageError);
          }
        }

        setBaseDetails({
          selectedFromList: urlAccentColor ? { overviewAccentColor: urlAccentColor } : null,
          singleProject,
          completeProjectData,
          employeeDirectory,
          deliveryMethodologyDirectory,
          estimatedGmRows,
          grossMarginRows,
          resolvedProjectSeqId,
          resolvedProjectId,
        });
        setError(null);

      } catch (err) {
        if (cancelled) return;
        setError(err?.message || extractBackendErrorMessage(err) || 'Failed to fetch project data');
        console.error('Failed to fetch project data:', err);
        setBaseDetails(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProjectData();

    return () => {
      cancelled = true;
    };
  }, [queryKey, reloadToken, userProfile]);

  const lazyLoadProgress = useCallback(async (projectId) => {
    if (progressLoaded || progressLoading || !projectId) return;
    try {
      setProgressLoading(true);
      setProgressError(null);
      const response = await UseAxios(
        getProjectProgressByProjectId(projectId, String(Date.now())),
      );
      const data = normalizeProjectProgressApiRows(
        extractProjectProgressApiRows(getResponseData(response, 'project progress')),
      );
      setProgressRows(data);
      setProgressLoaded(true);
    } catch (err) {
      setProgressError(err?.message || 'Failed to load progress history');
    } finally {
      setProgressLoading(false);
    }
  }, [progressLoaded, progressLoading]);

  const lazyLoadEfforts = useCallback(async (projectId) => {
    if (effortsLoaded || effortsLoading || !projectId) return;
    try {
      setEffortsLoading(true);
      setEffortsError(null);

      const [effortResponse, typeResponse, categoryResponse] = await Promise.all([
        UseAxios(getEffortEstimations({ proposal_id: projectId }, String(Date.now()))),
        !resourceTypes ? UseAxios(getResourceTypes()) : Promise.resolve(null),
        !resourceCategories ? UseAxios(getResourceCategories({ is_list: 1 })) : Promise.resolve(null),
      ]);

      const effortPayload = getResponseData(effortResponse, 'effort estimation');
      const effortData = extractEffortEstimationApiRows(effortPayload);
      const groupsData = extractEffortGroupsApiRows(effortPayload);

      setEffortRows(effortData);
      setEffortGroups(groupsData);

      if (typeResponse) {
        setResourceTypes(extractResourceTypeRows(getResponseData(typeResponse, 'resource types')));
      }
      if (categoryResponse) {
        setResourceCategories(extractResourceCategoryRows(getResponseData(categoryResponse, 'resource categories')));
      }

      setEffortsLoaded(true);
    } catch (err) {
      setEffortsError(err?.message || 'Failed to load efforts estimation');
    } finally {
      setEffortsLoading(false);
    }
  }, [effortsLoaded, effortsLoading, resourceTypes, resourceCategories]);

  const lazyLoadProjectUnits = useCallback(async (projectId) => {
    if (projectUnitsLoaded || projectUnitsLoading || !projectId) return;
    try {
      setProjectUnitsLoading(true);
      setProjectUnitsError(null);
      const response = await UseAxios(
        getProjectUnits({ projectId }, String(Date.now())),
      );
      const data = extractProjectUnitRows(getResponseData(response, 'project units'));
      setProjectUnits(data);
      setProjectUnitsLoaded(true);
    } catch (err) {
      setProjectUnitsError(err?.message || 'Failed to load project units');
    } finally {
      setProjectUnitsLoading(false);
    }
  }, [projectUnitsLoaded, projectUnitsLoading]);

  const lazyLoadCsat = useCallback((projectId) => {
    const normalizedProjectId = String(projectId || '').trim();
    if (
      csatCustomersLoaded
      && String(csatCustomersLoadedProjectId) === normalizedProjectId
    ) return Promise.resolve(csatCustomers);
    if (csatCustomersLoading && csatCustomersLoadPromiseRef.current) {
      return csatCustomersLoadPromiseRef.current;
    }

    const loadPromise = (async () => {
      if (!normalizedProjectId) return [];
      try {
        setCsatCustomersLoading(true);
        setCsatCustomersError(null);

        const response = await UseAxios(
          getCsatCustomerDetailsByProjectId(normalizedProjectId, String(Date.now())),
        );
        const csatCustomersResult = normalizeCsatCustomerRows(
          extractCsatCustomerRows(response?.data),
        ).filter((customer) => (
          customer.project_id === null
          || customer.project_id === undefined
          || String(customer.project_id) === normalizedProjectId
        ));

        setCsatCustomers(csatCustomersResult);
        setCsatCustomersLoaded(true);
        setCsatCustomersLoadedProjectId(normalizedProjectId);
        return csatCustomersResult;
      } catch (err) {
        setCsatCustomersError(err?.message || 'Failed to load CSAT customers');
        setCsatCustomers([]);
        setCsatCustomersLoaded(false);
        setCsatCustomersLoadedProjectId(null);
        return [];
      } finally {
        setCsatCustomersLoading(false);
        csatCustomersLoadPromiseRef.current = null;
      }
    })();

    csatCustomersLoadPromiseRef.current = loadPromise;
    return loadPromise;
  }, [csatCustomers, csatCustomersLoaded, csatCustomersLoadedProjectId, csatCustomersLoading]);

  const lazyLoadAudit = useCallback(() => {
    if (auditSectionLoaded || auditSectionLoading || !baseDetails) return;
    setAuditSectionLoading(true);
    requestAnimationFrame(() => {
      try {
        const result = computeAuditScoreData(baseDetails._mergedBaseData);
        setAuditSectionData(result);
        setAuditSectionLoaded(true);
      } catch (err) {
        console.error('Failed to compute audit section data:', err);
      } finally {
        setAuditSectionLoading(false);
      }
    });
  }, [auditSectionLoaded, auditSectionLoading, baseDetails]);

  const lazyLoadRisk = useCallback(() => {
    if (riskSectionLoaded || riskSectionLoading || !baseDetails) return;
    setRiskSectionLoading(true);
    requestAnimationFrame(() => {
      try {
        const result = computeRiskRegisterData(baseDetails._mergedBaseData);
        setRiskSectionData(result);
        setRiskSectionLoaded(true);
      } catch (err) {
        console.error('Failed to compute risk section data:', err);
      } finally {
        setRiskSectionLoading(false);
      }
    });
  }, [riskSectionLoaded, riskSectionLoading, baseDetails]);

  const lazyLoadResources = useCallback(async () => {
    if (resourceSectionLoaded || resourceSectionLoading || !baseDetails) return;
    try {
      setResourceSectionLoading(true);

      const projectId = baseDetails.resolvedProjectId;
      let currentResourceTypes = resourceTypes;
      let currentProjectUnits = projectUnits;
      const promises = [];

      if (!currentResourceTypes) {
        promises.push(
          UseAxios(getResourceTypes()).then((res) => {
            currentResourceTypes = extractResourceTypeRows(getResponseData(res, 'resource types'));
            setResourceTypes(currentResourceTypes);
          }),
        );
      }

      if (!currentProjectUnits && projectId) {
        promises.push(
          UseAxios(getProjectUnits({ projectId }, String(Date.now()))).then((res) => {
            currentProjectUnits = extractProjectUnitRows(getResponseData(res, 'project units'));
            setProjectUnits(currentProjectUnits);
            setProjectUnitsLoaded(true);
          }),
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      const mergedBaseDataWithTypes = {
        ...baseDetails._mergedBaseData,
        resource_type: currentResourceTypes,
        projectUnitList: currentProjectUnits,
      };

      const result = computeResourceData(mergedBaseDataWithTypes);
      setResourceSectionData(result);
      setResourceSectionLoaded(true);
    } catch (err) {
      console.error('Failed to load resource types, project units, or compute resource section data:', err);
    } finally {
      setResourceSectionLoading(false);
    }
  }, [
    resourceSectionLoaded,
    resourceSectionLoading,
    baseDetails,
    resourceTypes,
    projectUnits,
  ]);

  const lazyLoadTbhResources = useCallback(() => {
    if (tbhSectionLoaded || tbhSectionLoading || !baseDetails) return;
    setTbhSectionLoading(true);
    requestAnimationFrame(() => {
      try {
        const result = computeTbhResources(baseDetails._mergedBaseData);
        setTbhSectionData(result);
        setTbhSectionLoaded(true);
      } catch (err) {
        console.error('Failed to compute TBH resources section data:', err);
      } finally {
        setTbhSectionLoading(false);
      }
    });
  }, [tbhSectionLoaded, tbhSectionLoading, baseDetails]);

  const refetchProjectData = useCallback(() => {
    setReloadToken((prev) => prev + 1);
    setProgressRows(null);
    setProgressLoaded(false);
    setEffortRows(null);
    setEffortGroups(null);
    setEffortsLoaded(false);
    setProjectUnits(null);
    setProjectUnitsLoaded(false);
    setCsatCustomers([]);
    setCsatCustomersLoaded(false);
    setAuditSectionData(null);
    setAuditSectionLoaded(false);
    setRiskSectionData(null);
    setRiskSectionLoaded(false);
    setResourceSectionData(null);
    setResourceSectionLoaded(false);
    setTbhSectionData(null);
    setTbhSectionLoaded(false);
  }, []);

  useEffect(() => {
    if (!baseDetails) return;

    if (auditSectionLoaded) {
      try {
        const result = computeAuditScoreData(baseDetails._mergedBaseData);
        setAuditSectionData(result);
      } catch (err) {
        console.error('Failed to re-compute audit section data:', err);
      }
    }

    if (riskSectionLoaded) {
      try {
        const result = computeRiskRegisterData(baseDetails._mergedBaseData);
        setRiskSectionData(result);
      } catch (err) {
        console.error('Failed to re-compute risk section data:', err);
      }
    }

    if (resourceSectionLoaded) {
      try {
        const result = computeResourceData(baseDetails._mergedBaseData);
        setResourceSectionData(result);
      } catch (err) {
        console.error('Failed to re-compute resource section data:', err);
      }
    }

    if (tbhSectionLoaded) {
      try {
        const result = computeTbhResources(baseDetails._mergedBaseData);
        setTbhSectionData(result);
      } catch (err) {
        console.error('Failed to re-compute TBH resources section data:', err);
      }
    }
  }, [
    baseDetails,
    auditSectionLoaded,
    riskSectionLoaded,
    resourceSectionLoaded,
    tbhSectionLoaded,
  ]);

  const projectData = useMemo(() => {
    if (!baseDetails) return null;

    const {
      selectedFromList,
      singleProject,
      completeProjectData,
      employeeDirectory,
      deliveryMethodologyDirectory,
      estimatedGmRows,
      grossMarginRows,
      resolvedProjectSeqId,
    } = baseDetails;

    const hydratedCompleteProjectData = applySupplementalResultsToProjectData({
      effortResult: effortRows ? { data: effortRows, error: null } : null,
      effortGroupsResult: effortGroups ? { data: effortGroups, error: null } : null,
      hydratedCompleteProjectData: completeProjectData,
      projectUnitsResult: projectUnits ? { data: projectUnits, error: null } : null,
      resourceCategoryResult: resourceCategories ? { data: resourceCategories, error: null } : null,
      resourceTypeResult: resourceTypes ? { data: resourceTypes, error: null } : null,
    });

    const { viewModel, mergedBaseData } = buildSupplementalProjectViewModel({
      selectedFromList,
      singleProject,
      completeProjectData,
      hydratedCompleteProjectData,
      employeeDirectory,
      deliveryMethodologyDirectory,
      estimatedGmRows,
      grossMarginRows,
      projectProgressRows: progressRows || [],
    });

    // Store mergedBaseData in baseDetails for lazy section computation
    // This avoids re-computing it when sections are expanded
    baseDetails._mergedBaseData = mergedBaseData;

    const { actualProjectSeqId, actualProjectId } = buildResolvedProjectIdentity({
      selectedFromList,
      singleProject,
      hydratedCompleteProjectData,
      viewModelWithResolvedEgm: viewModel,
      resolvedProjectSeqId,
    });

    return {
      ...viewModel,
      project_seq_id: actualProjectSeqId,
      project_id: actualProjectId,
      project_name: viewModel?.projectHeader?.title || '',
      projectName: viewModel?.projectHeader?.title || '',
      // Merge in lazy-loaded section data when available
      ...(auditSectionData ? auditSectionData : {}),
      ...(riskSectionData ? { riskRegisterData: riskSectionData } : {}),
      ...(resourceSectionData ? {
        resourceAllocationData: resourceSectionData.resourceAllocationData,
        resourceCountData: resourceSectionData.resourceCountData,
      } : {}),
      ...(tbhSectionData ? { tbhResources: tbhSectionData } : {}),
    };
  }, [
    baseDetails, progressRows, effortRows, effortGroups, resourceTypes, resourceCategories, projectUnits,
    auditSectionData, riskSectionData, resourceSectionData, tbhSectionData,
  ]);

  return {
    projectData,
    loading,
    error,
    refetchProjectData,

    // CSAT customers lazy-loaded state and trigger
    csatCustomers,
    csatCustomersLoading,
    csatCustomersError,
    lazyLoadCsat,

    // Progress history lazy-loaded state and trigger
    progressLoading,
    progressError,
    lazyLoadProgress,

    // Efforts lazy-loaded state and trigger
    effortsLoading,
    effortsError,
    lazyLoadEfforts,

    // Project units lazy-loaded state and trigger
    projectUnitsLoading,
    projectUnitsError,
    lazyLoadProjectUnits,

    // Audit section lazy-loaded state and trigger
    auditSectionLoading,
    lazyLoadAudit,

    // Risk section lazy-loaded state and trigger
    riskSectionLoading,
    lazyLoadRisk,

    // Resource section (count + details + allocation) lazy-loaded state and trigger
    resourceSectionLoading,
    lazyLoadResources,

    // TBH resources section lazy-loaded state and trigger
    tbhSectionLoading,
    lazyLoadTbhResources,
  };
};

export default useProjectDetailsData;
/* eslint-disable id-length, max-lines, max-lines-per-function */
